import React, { useEffect, useReducer, useState } from "react";
import { Grid, Box, Typography, Checkbox, Button, Divider, Snackbar, IconButton, CircularProgress } from "@mui/material";
import { DatePicker } from '../../Common/DatePicker/DatePicker';
import rosterBackendService from '../../../services/RosterBackendService';
import MuiAlert from '@mui/material/Alert';
import { Result } from "true-myth";
import { APIError } from '@beable/mwm-commons-endpoint-client';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDialog from "../../Common/ConfirmDialog";
import moment from "moment";
import { isDate } from "date-fns";
import { SitePauseDateDTO } from "@beable/roster-backend-client-v1";
import { YYYY_MM_DD_HH_mm_ss } from "@beable/mwm-commons-date-time";

interface PauseDatesProps {
  siteId: number;
}

interface PauseDate {
  startDate: YYYY_MM_DD_HH_mm_ss;
  endDate: YYYY_MM_DD_HH_mm_ss;
  active: boolean;
  checked?: boolean;
}

interface PauseDatesPayload {
  optionId: string | undefined;
  siteId: number | undefined;
  pauseDates: PauseDate[] | undefined;
}

const dateTimeFormat = 'yyyy-MM-DD HH:mm:ss';

const initialPauseDate: PauseDate = {
  startDate: new YYYY_MM_DD_HH_mm_ss(moment().startOf('day').format(dateTimeFormat)),
  endDate: new YYYY_MM_DD_HH_mm_ss(moment().endOf('day').format(dateTimeFormat)),
  active: true
}

const pauseDatesReducer = (state: PauseDatesPayload, newState: PauseDatesPayload): PauseDatesPayload => {
  return {
    ...state,
    ...newState
  }
}

const initialPauseDatesPayload: PauseDatesPayload = {
  siteId: undefined,
  optionId: undefined,
  pauseDates: undefined,
}

export const PauseDates = ({ siteId }: PauseDatesProps) => {
  const [pauseDatesPayload, setPauseDatesPayload] = useReducer(pauseDatesReducer, { ...initialPauseDatesPayload });
  const [newPauseDate, setNewPauseDate] = useState<PauseDate>({ ...initialPauseDate });
  const [isLoading, setIsLoading] = useState(false);
  const [shouldUpdatePauseDates, setShouldUpdatePauseDates] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [messageSettings, setMessageSettings] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  useEffect(() => {
    resetPauseDatesPayload();
    const getPauseDates = async () => {
      try {
        isLoadingData();
        const response = await rosterBackendService.getSitePauseDatesWithParse(siteId);
        finishedLoadingData();

        if (response.isOk()) {
          setPauseDatesPayload({ ...response.value });
        }
      } catch (error) {
        console.error(error);
        finishedLoadingData();
      }
    }
    getPauseDates();
  }, [siteId, shouldUpdatePauseDates]);

  const closeMessage = () => {
    setMessageSettings({
      open: false,
      message: "",
      severity: messageSettings.severity,
    });
  };

  const isLoadingData = () => {
    setIsLoading(true);
  }

  const finishedLoadingData = () => {
    setIsLoading(false);
  }

  const resetPauseDatesPayload = () => {
    setPauseDatesPayload({ ...initialPauseDatesPayload, siteId });
  }

  const resetNewPauseDate = () => {
    setNewPauseDate({ ...initialPauseDate });
  }

  const validateResponse = (response: Result<any, APIError<string>>, messageOnSuccess: string, messageOnError: string) => {
    const messageSettings = {
      open: true,
      message: messageOnSuccess,
      severity: 'success',
    }

    if (response.isErr()) {
      messageSettings.message = messageOnError;
      messageSettings.severity = 'error';
      if (response.error.result.isJust()) {
        messageSettings.message += ` ${response.error.result.value.message}`;
      } else {
        messageSettings.message += ` ${response.error.message}`;
      }
    }
    setMessageSettings(messageSettings);
    return messageSettings;
  }

  const shouldEnableDelete = () => {
    return (pauseDatesPayload.optionId || (pauseDatesPayload.pauseDates && pauseDatesPayload.pauseDates.length > 0));
  }

  const getMinEndDate = (startDate: Date | string) => {
    const momentStartDate = moment(startDate).startOf('day');
    const today = moment();
    return momentStartDate.isAfter(today, 'day') ? momentStartDate : today;
  }

  const getMinStartDate = (endDate: Date | string) => {
    const momentEndDate = moment(endDate, 'yyyy-MM-DD HH:mm:ss', true);
    const today = moment();
    return momentEndDate.isBefore(today, 'day') ? momentEndDate : today;
  }

  const savePauseDates = async (pauseDatesPayload: PauseDatesPayload, successMessage: string, errorMessage: string) => {
    try {
      isLoadingData();
      if (pauseDatesPayload.optionId) {
        if (pauseDatesPayload.pauseDates && pauseDatesPayload.pauseDates.length > 0) {
          const response = await rosterBackendService.updateSitePauseDates(pauseDatesPayload.siteId as number, pauseDatesPayload.optionId, { pauseDates: pauseDatesPayload.pauseDates as SitePauseDateDTO[] });
          finishedLoadingData();
          const validation = validateResponse(response, successMessage, errorMessage);
          if (validation.severity === 'error') return;
        } else {
          const response = await rosterBackendService.deleteSitePauseDates(pauseDatesPayload.optionId);
          finishedLoadingData();
          const validation = validateResponse(response, successMessage, errorMessage);
          if (validation.severity === 'error') return;
        }
      } else if (pauseDatesPayload.pauseDates && pauseDatesPayload.pauseDates.length > 0) {
        const response = await rosterBackendService.createSitePauseDates(siteId, { pauseDates: pauseDatesPayload.pauseDates as [] });
        finishedLoadingData();
        const validation = validateResponse(response, successMessage, errorMessage);
        if (validation.severity === 'error') return;
        if (validation.severity === 'success') {
          setShouldUpdatePauseDates(!shouldUpdatePauseDates);
        }
      }
      finishedLoadingData();
      setPauseDatesPayload({ ...pauseDatesPayload });
    } catch (error) {
      console.error(error);
      finishedLoadingData();
    }
  }

  const clonePauseDatesPayload = (pauseDatesPayload: PauseDatesPayload): PauseDatesPayload => {
    const pauseDatesPayloadRaw = JSON.parse(JSON.stringify(pauseDatesPayload));
    const newPauseDatesPayload: PauseDatesPayload = {
      optionId: pauseDatesPayloadRaw['optionId'],
      siteId: pauseDatesPayloadRaw['siteId'],
      pauseDates: undefined,
    };
    if (pauseDatesPayloadRaw['pauseDates'] !== undefined) {
      newPauseDatesPayload.pauseDates = pauseDatesPayloadRaw['pauseDates'].map((item: any) => ({
        startDate: new YYYY_MM_DD_HH_mm_ss(item.startDate as string),
        endDate: new YYYY_MM_DD_HH_mm_ss(item.endDate as string),
        active: item.active,
      }))
    }
    return newPauseDatesPayload;
  }

  // Handlers
  const handleDateChange = (newDate: Date, pauseDatesPayload: PauseDatesPayload, index: number, dateName: string) => {
    const newPauseDatesPayload = clonePauseDatesPayload(pauseDatesPayload);
    const pauseDate: PauseDate = (newPauseDatesPayload.pauseDates as PauseDate[])[index];

    if (dateName === 'startDate') {
      const momentNewDate = moment(newDate);
      const momentStartDate = moment(pauseDate.startDate.toString());
      if (momentNewDate.isSame(momentStartDate, 'day')) return;

      pauseDate.startDate = new YYYY_MM_DD_HH_mm_ss(momentNewDate.startOf('day').format(dateTimeFormat));
    }
    if (dateName === 'endDate') {
      const momentNewDate = moment(newDate);
      const momentEndDate = moment(pauseDate.endDate.toString());
      if (momentNewDate.isSame(momentEndDate, 'day')) return;
      pauseDate.endDate = new YYYY_MM_DD_HH_mm_ss(momentNewDate.endOf('day').format(dateTimeFormat));
    }
    savePauseDates(newPauseDatesPayload, 'Pause date updated successfully', 'Failed to update Pause Dates: ');
  }

  const handleActiveChange = (pauseDate: PauseDate) => {
    pauseDate.active = !pauseDate.active;
    savePauseDates(pauseDatesPayload, 'Pause date updated successfully', 'Failed to update Pause Dates: ');
  }

  const handleNewPauseDateChange = (keyName: string, newValue?: Date) => {
    if (keyName === 'startDate') {
      newPauseDate.startDate = new YYYY_MM_DD_HH_mm_ss(moment(newValue).startOf('day').format(dateTimeFormat));
    }
    if (keyName === 'endDate') {
      newPauseDate.endDate = new YYYY_MM_DD_HH_mm_ss(moment(newValue).endOf('day').format(dateTimeFormat));
    }
    if (keyName === 'active') {
      newPauseDate.active = !newPauseDate.active;
    }
    setNewPauseDate({ ...newPauseDate });
  }

  const handleAddNewPauseDate = () => {
    const newPauseDatesPayload = clonePauseDatesPayload(pauseDatesPayload);
    if (newPauseDatesPayload.pauseDates && newPauseDatesPayload.pauseDates.length >= 0) {
      const newStartDate = moment(newPauseDate.startDate.toString());
      const newEndDate = moment(newPauseDate.endDate.toString());

      if (
        newPauseDatesPayload.pauseDates.some(item => newStartDate.isSame(moment(item.startDate.toString())) && newEndDate.isSame(moment(item.endDate.toString())))
      ) {
        setMessageSettings({
          open: true,
          message: 'Pause Date already exists',
          severity: 'error',
        });
        return;
      }

      newPauseDatesPayload.pauseDates.unshift(newPauseDate);
    } else {
      newPauseDatesPayload.pauseDates = [newPauseDate];
    }
    resetNewPauseDate();
    savePauseDates(newPauseDatesPayload, 'Pause date created successfully', 'Failed to create Pause Dates: ');
  }

  const handleDeletePauseDate = (index: number) => {
    if (pauseDatesPayload.pauseDates && pauseDatesPayload.pauseDates.length > 0) {
      pauseDatesPayload.pauseDates.splice(index, (index >= 0) as boolean & 1);
      savePauseDates(pauseDatesPayload, 'Pause date deleted successfully', 'Failed to delete Pause Dates: ');
    }
  }

  const handleDeleteAllPauseDates = async () => {
    if (pauseDatesPayload.optionId) {
      const response = await rosterBackendService.deleteSitePauseDates(pauseDatesPayload.optionId);
      const validation = validateResponse(response, 'Pause dates deleted successfully', 'Failed to delete Pause Dates');
      if (validation.severity === 'success') {
        resetPauseDatesPayload();
      }
    } else {
      resetPauseDatesPayload();
    }
  }

  // Components
  const NewPauseDate = () => {
    return (
      <Box sx={{ marginBottom: '32px' }}>
        <Typography>Add New Pause Date</Typography>
        <Box sx={{ paddingTop: '24px' }}>
          <Grid
            container
            spacing={3}
            sx={{
              justifyContent: "center",
              alignItems: "center",
              marginBottom: '24px'
            }}>
            <Grid id="start-date">
              <DatePicker
                label='Start Date'
                value={newPauseDate.startDate.toDate()}
                onChange={(selectedDate: Date) => handleNewPauseDateChange('startDate', selectedDate)}
              />
            </Grid>
            <Grid id="end-date">
              <DatePicker
                label='End Date'
                value={newPauseDate.endDate.toDate()}
                onChange={(selectedDate: Date) => handleNewPauseDateChange('endDate', selectedDate)}
              />
            </Grid>
            <Grid>
              <Box>
                Active
              </Box>
              <Checkbox
                checked={newPauseDate.active}
                onChange={() => handleNewPauseDateChange('active')}
                name="test"
                color="primary"
                sx={{ padding: '4px 0px' }}
              />
            </Grid>
          </Grid>
          <Grid
            container
            sx={{
              justifyContent: "center",
              alignItems: "center"
            }}>
            <Grid>
              <Button
                variant={"contained"}
                color={"primary"}
                onClick={() => handleAddNewPauseDate()}
                disabled={isLoading}
              >
                Add Pause Date
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  }

  const PauseDates = () => {
    return (
      <Box sx={{
        position: 'relative'
      }}>
        <Typography>Pause Dates</Typography>
        {
          (pauseDatesPayload.pauseDates && pauseDatesPayload.pauseDates.length > 0) ?
            <Box sx={{ padding: '24px 0px' }}>
              <Grid container spacing={2} sx={{
                marginBottom: '10px',
                alignItems: "center"
              }}>
                <Grid sx={{ display: 'flex', justifyContent: 'end' }} size={3}>
                  <IconButton onClick={() => setOpenConfirmDialog(true)} size="large">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Grid>
                <Grid>
                  <Button onClick={() => setOpenConfirmDialog(true)} disabled={!shouldEnableDelete()}>Delete all</Button>
                </Grid>
              </Grid>
              {
                pauseDatesPayload?.pauseDates?.map((item, index) =>
                  <Grid
                    key={index}
                    container
                    spacing={2}
                    sx={{
                      justifyContent: "center",
                      alignItems: "center"
                    }}>
                    <Grid sx={{ display: 'flex', justifyContent: 'end' }} size={1}>
                      <IconButton onClick={() => handleDeletePauseDate(index)} size="large">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Grid>
                    <Grid size={3}>
                      <DatePicker
                        label='Start Date'
                        value={item.startDate.toDate()}
                        onChange={(selectedDate: Date) => {
                          handleDateChange(selectedDate, pauseDatesPayload, index, 'startDate');
                        }}
                      />
                    </Grid>
                    <Grid size={3}>
                      <DatePicker
                        label='End Date'
                        value={item.endDate.toDate()}
                        onChange={(selectedDate: Date) => {
                          handleDateChange(selectedDate, pauseDatesPayload, index, 'endDate');
                        }}
                      />
                    </Grid>
                    <Grid size={1}>
                      <Box>
                        Active
                      </Box>
                      <Checkbox
                        checked={item.active}
                        onChange={() => handleActiveChange(item)}
                        color="primary"
                        sx={{ padding: '4px 0px' }}
                      />
                    </Grid>
                  </Grid>
                )
              }
            </Box> :
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                paddingBottom: '24px'
              }}>
              <Typography>No Pause Dates to display...</Typography>
            </Box>
        }
        {
          isLoading ?
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: '#f5f5f57a',
            }}>
              <Box sx={{
                display: 'flex',
                width: '100%',
                height: '100%',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                <CircularProgress disableShrink />
              </Box>
            </Box>
            :
            null
        }
      </Box>
    );
  }

  const Message = () => {
    return (
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={messageSettings.open && !isLoading}
        autoHideDuration={5000}
        onClose={closeMessage}
        sx={{ position: 'absolute', bottom: '0px' }}
      >
        <MuiAlert
          severity={messageSettings.severity as "error" | "success" | "info" | "warning"}
          // elevation={6}
          variant="filled"
        >
          {messageSettings.message}
        </MuiAlert>
      </Snackbar>
    )
  }

  // Render
  return (
    <Box id='site-pause-dates' sx={{ margin: '16px 10px' }} >
      <NewPauseDate />
      <Divider sx={{ margin: "24px 0px" }} />
      <PauseDates />
      <Message />
      <ConfirmDialog
        open={openConfirmDialog}
        header='Confirm Deletion'
        message={`Are you sure you want to delete all the Pause Dates?`}
        confirmString="Continue"
        cancelString="Cancel"
        onCancel={() => setOpenConfirmDialog(false)}
        onConfirm={() => {
          setOpenConfirmDialog(false);
          handleDeleteAllPauseDates();
        }}
      />
    </Box>
  )
}